import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone, TableWrap } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, upload } from '../lib/api.ts';
import { dateTime, day, money, moneyIn, parseMoney, parseRate, rateText, todayIso } from '../lib/format.ts';
import { native } from '../lib/native.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * Group consolidation: the companies of a group, one group chart of accounts, and per period
 * each company's trial balance — read from this business's own books, or sent as a file by the
 * others — translated, mapped, eliminated and added up into the group's statements.
 */

type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';
const TYPES: AccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];
type Method = 'full' | 'proportional';
type Source = 'own' | 'import';
const FULL = 10_000;

interface Entity {
  id: string;
  name: string;
  currency: string;
  ownership: number;
  method: Method;
  source: Source;
  active: boolean;
  sequence: number;
  mapped: number;
}
interface PeriodRow {
  id: string;
  name: string;
  date_from: string;
  date_to: string;
  state: 'draft' | 'closed';
  closed_at: string | null;
  loaded: number;
  eliminations: number;
}
interface Overview {
  currency: string;
  ledger: boolean;
  entities: Entity[];
  accounts: number;
  periods: PeriodRow[];
}
interface GroupAccount {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  mapped: number;
}
interface PeriodEntity extends Entity {
  load: { source: string; lines: number; loaded_at: string } | null;
  totals: { debit: number; credit: number };
  foreign: boolean;
  rates: { closing: number; average: number } | null;
  suggested: { closing: number | null; average: number | null } | null;
}
interface Elimination {
  id: string;
  memo: string;
  lines: { account: string; debit: number; credit: number }[];
  total: number;
  by_name: string | null;
  created_at: string;
}
interface PeriodView {
  period: PeriodRow;
  currency: string;
  entities: PeriodEntity[];
  eliminations: Elimination[];
}
interface ReportRow {
  account: string | null;
  code: string;
  name: string;
  type: AccountType | null;
  entities: Record<string, number>;
  elimination: number;
  total: number;
}
interface Check {
  kind: 'no_balance' | 'no_rate' | 'unmapped' | 'unbalanced';
  entity: string;
  name: string;
  count?: number;
  codes?: string[];
}
interface Report {
  currency: string;
  entities: { id: string; name: string; currency: string; ownership: number; method: Method; profit?: number; net_assets?: number; cta?: number }[];
  rows: ReportRow[];
  cta: { total: number; entities: Record<string, number> };
  pl: { income: number; expense: number; profit: number; nci: number; owners: number };
  bs: { assets: number; liabilities: number; equity: number; profit: number; translation: number; nci: number; total_right: number; difference: number; unmapped: number };
  eliminations: number;
  checks: Check[];
}

const pctText = (hundredths: number) => `${(hundredths / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const typeLabel = (k: AccountType) => t(`cons.type_${k}` as MessageKey);
const STATE: Record<PeriodRow['state'], { tone: Tone; icon: IconName }> = { draft: { tone: 'info', icon: 'edit' }, closed: { tone: 'ok', icon: 'lock' } };
const toMillionths = (n: number) => Math.round(n * 1_000_000);

function Crumbs({ items }: { items: { to?: string; label: string }[] }) {
  return (
    <nav className="crumbs" aria-label={t('cons.crumbs')}>
      {items.map((x, i) => (
        <span key={i} className="crumb">
          {i ? <Icon name="chevronRight" size={14} /> : null}
          {x.to ? <Link to={x.to}>{x.label}</Link> : <span>{x.label}</span>}
        </span>
      ))}
    </nav>
  );
}

// ---- overview -------------------------------------------------------------------------------

type Tab = 'periods' | 'companies' | 'chart' | 'send';

export function Consolidation() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const manage = can('consolidation.manage');
  const { data: o, reload } = useAction<Overview>('consolidation.overview');
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: 'periods', icon: 'calendar', show: true },
    { key: 'companies', icon: 'store', show: true },
    { key: 'chart', icon: 'book', show: true },
    { key: 'send', icon: 'upload', show: can('consolidation.export') },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'periods') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'periods') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const [periodDlg, setPeriodDlg] = useState(false);
  const ready = o ? o.entities.length > 0 && o.accounts > 0 : false;
  return (
    <Shell>
      <div className="page reports cn-page">
        <div className="page-head">
          <div>
            <h1>{t('cons.title')}</h1>
            <p className="muted">{t('cons.lead')}</p>
          </div>
          {manage && tab === 'periods' ? (
            <Button variant="primary" icon="plus" disabled={!ready} onClick={() => setPeriodDlg(true)}>
              {t('cons.new_period')}
            </Button>
          ) : null}
        </div>
        {o ? (
          <div className="rep-kpis">
            <Kpi label={t('cons.k_companies')} value={String(o.entities.filter((e) => e.active).length)} hint={t('cons.k_companies_hint', { currency: o.currency })} />
            <Kpi label={t('cons.k_accounts')} value={String(o.accounts)} hint={t('cons.k_accounts_hint')} />
            <Kpi label={t('cons.k_periods')} value={String(o.periods.length)} hint={tn('cons.k_closed', o.periods.filter((p) => p.state === 'closed').length)} />
            <Kpi
              label={t('cons.k_unmapped')}
              value={String(o.entities.filter((e) => e.active && e.mapped === 0).length)}
              hint={t('cons.k_unmapped_hint')}
              warn={o.entities.some((e) => e.active && e.mapped === 0)}
            />
          </div>
        ) : null}
        <div className="segmented tabs" role="tablist" aria-label={t('cons.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`cons.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : tab === 'periods' ? (
          <PeriodsTab o={o} manage={manage} onSetup={() => setTab(o.entities.length ? 'chart' : 'companies')} onNew={() => setPeriodDlg(true)} />
        ) : tab === 'companies' ? (
          <CompaniesTab o={o} manage={manage} onChange={reload} />
        ) : tab === 'chart' ? (
          <ChartTab o={o} manage={manage} onChange={reload} />
        ) : (
          <SendTab />
        )}
        {periodDlg ? <PeriodDialog period={null} onClose={() => setPeriodDlg(false)} onSaved={reload} /> : null}
      </div>
    </Shell>
  );
}

function PeriodsTab({ o, manage, onSetup, onNew }: { o: Overview; manage: boolean; onSetup: () => void; onNew: () => void }) {
  const navigate = useNavigate();
  const active = o.entities.filter((e) => e.active).length;
  if (!o.entities.length || !o.accounts)
    return (
      <section className="panel">
        <EmptyState
          icon="branch"
          title={t('cons.setup_title')}
          body={t('cons.setup_body')}
          action={manage ? <Button icon="chevronRight" onClick={onSetup}>{o.entities.length ? t('cons.setup_chart') : t('cons.setup_companies')}</Button> : undefined}
        />
      </section>
    );
  if (!o.periods.length)
    return (
      <section className="panel">
        <EmptyState icon="calendar" title={t('cons.no_periods')} body={t('cons.no_periods_body')} action={manage ? <Button icon="plus" onClick={onNew}>{t('cons.new_period')}</Button> : undefined} />
      </section>
    );
  return (
    <section className="panel">
      <Table
        head={[t('cons.period'), t('cons.dates'), t('cons.state'), t('cons.balances_in'), t('cons.eliminations')]}
        left={[1]}
        phoneHide={[1, 4]}
        rows={o.periods.map((p) => ({
          key: p.id,
          onClick: () => navigate(`/consolidation/${p.id}`),
          cells: [
            <Link to={`/consolidation/${p.id}`}>{p.name}</Link>,
            `${day(p.date_from)} – ${day(p.date_to)}`,
            <StatusBadge tone={STATE[p.state].tone} icon={STATE[p.state].icon} label={t(`cons.st_${p.state}` as MessageKey)} />,
            p.loaded >= active ? `${p.loaded} / ${active}` : <strong className="cn-warn">{`${p.loaded} / ${active}`}</strong>,
            String(p.eliminations),
          ],
        }))}
      />
    </section>
  );
}

function CompaniesTab({ o, manage, onChange }: { o: Overview; manage: boolean; onChange: () => Promise<unknown> }) {
  const navigate = useNavigate();
  const [edit, setEdit] = useState<Entity | 'new' | null>(null);
  return (
    <section className="panel">
      <div className="cn-panel-head">
        <div>
          <h2>{t('cons.companies')}</h2>
          <p className="muted small">{t('cons.companies_lead', { currency: o.currency })}</p>
        </div>
        {manage ? (
          <Button icon="plus" onClick={() => setEdit('new')}>
            {t('cons.add_company')}
          </Button>
        ) : null}
      </div>
      {!o.entities.length ? (
        <EmptyState icon="store" title={t('cons.no_companies')} body={t('cons.no_companies_body')} />
      ) : (
        <Table
          head={[t('cons.company'), t('cons.currency'), t('cons.ownership'), t('cons.method'), t('cons.source'), t('cons.mapped'), '']}
          left={[4]}
          phoneHide={[1, 2, 3, 4, 5]}
          rows={o.entities.map((e) => ({
            key: e.id,
            cells: [
              <span className="cn-company">
                <strong>{e.name}</strong>
                {!e.active ? <span className="muted small">{t('cons.inactive')}</span> : null}
              </span>,
              e.currency,
              pctText(e.ownership),
              t(`cons.m_${e.method}` as MessageKey),
              t(`cons.src_${e.source}` as MessageKey),
              e.mapped ? String(e.mapped) : <strong className="cn-warn">0</strong>,
              <span className="cn-row-actions">
                <Button size="sm" variant="ghost" icon="link" onClick={() => navigate(`/consolidation/mapping/${e.id}`)}>
                  {t('cons.map_accounts')}
                </Button>
                {manage ? <IconButton icon="edit" label={t('cons.edit_company_n', { name: e.name })} onClick={() => setEdit(e)} /> : null}
              </span>,
            ],
          }))}
        />
      )}
      {edit ? <EntityDialog entity={edit === 'new' ? null : edit} o={o} onClose={() => setEdit(null)} onSaved={onChange} /> : null}
    </section>
  );
}

function EntityDialog({ entity, o, onClose, onSaved }: { entity: Entity | null; o: Overview; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const hasOwn = o.entities.some((e) => e.source === 'own' && e.id !== entity?.id);
  const [f, setF] = useState({
    name: entity?.name ?? '',
    currency: entity?.currency ?? o.currency,
    ownership: entity ? String(entity.ownership / 100) : '100',
    method: entity?.method ?? ('full' as Method),
    source: entity?.source ?? ((!hasOwn && o.ledger ? 'own' : 'import') as Source),
    active: entity?.active ?? true,
  });
  const [drop, setDrop] = useState(false);
  const own = parseFloat(f.ownership.replace(',', '.'));
  const ownOk = Number.isFinite(own) && own > 0 && own <= 100;
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  return (
    <Dialog
      title={entity ? t('cons.edit_company') : t('cons.add_company')}
      onClose={onClose}
      footer={
        <>
          {entity ? (
            <Button variant="ghost" icon="trash" className="cn-left" onClick={() => setDrop(true)}>
              {t('cons.remove_company')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim() || !ownOk || !/^[A-Za-z]{3}$/.test(f.currency)}
            onClick={() =>
              void run(async () => {
                await action('consolidation.save_entity', { id: entity?.id ?? null, ...f, ownership: Math.round(own * 100) });
                await onSaved();
                onClose();
              }, t('cons.company_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('cons.company_name')} required>
          <input className="input" value={f.name} maxLength={120} placeholder={t('cons.company_name_ph')} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label={t('cons.source')} hint={t(`cons.src_${f.source}_hint` as MessageKey)}>
          <select className="input" value={f.source} onChange={(e) => set({ source: e.target.value as Source, currency: e.target.value === 'own' ? o.currency : f.currency })}>
            <option value="own" disabled={hasOwn || !o.ledger}>
              {t('cons.src_own')}
            </option>
            <option value="import">{t('cons.src_import')}</option>
          </select>
        </Field>
        <Field label={t('cons.currency')} hint={t('cons.currency_hint')} required>
          <input className="input cn-code" value={f.currency} maxLength={3} disabled={f.source === 'own'} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={t('cons.ownership_pct')} error={f.ownership && !ownOk ? t('cons.ownership_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={f.ownership} onChange={(e) => set({ ownership: e.target.value })} />
        </Field>
      </div>
      <fieldset className="cn-choices">
        <legend>{t('cons.method')}</legend>
        {(['full', 'proportional'] as Method[]).map((m) => (
          <label key={m} className={`cn-choice${f.method === m ? ' on' : ''}`}>
            <input type="radio" name="method" checked={f.method === m} onChange={() => set({ method: m })} />
            <span>
              <strong>{t(`cons.m_${m}` as MessageKey)}</strong>
              <span className="muted small">{t(`cons.m_${m}_hint` as MessageKey)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {entity ? (
        <label className="check-row">
          <input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} />
          <span>{t('cons.active_company')}</span>
        </label>
      ) : null}
      {drop && entity ? (
        <Confirm
          title={t('cons.remove_company_title', { name: entity.name })}
          body={t('cons.remove_company_body')}
          confirm={t('cons.remove_company')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('consolidation.delete_entity', { id: entity.id });
              await onSaved();
              onClose();
            }, t('cons.company_removed'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function ChartTab({ o, manage, onChange }: { o: Overview; manage: boolean; onChange: () => Promise<unknown> }) {
  const { data, reload } = useAction<GroupAccount[]>('consolidation.chart');
  const { busy, run } = useSave();
  const [edit, setEdit] = useState<GroupAccount | 'new' | null>(null);
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ?? []).filter((a) => !s || a.code.toLowerCase().includes(s) || a.name.toLowerCase().includes(s));
  }, [data, q]);
  const changed = async () => {
    await reload();
    await onChange();
  };
  return (
    <section className="panel">
      <div className="cn-panel-head">
        <div>
          <h2>{t('cons.chart')}</h2>
          <p className="muted small">{t('cons.chart_lead')}</p>
        </div>
        {manage ? (
          <span className="cn-row-actions">
            {o.ledger ? (
              <Button
                icon="copy"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    const r = await action<{ added: number }>('consolidation.copy_chart', {});
                    await changed();
                    return r;
                  }, t('cons.chart_copied'))
                }
              >
                {t('cons.copy_chart')}
              </Button>
            ) : null}
            <Button icon="plus" onClick={() => setEdit('new')}>
              {t('cons.add_account')}
            </Button>
          </span>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : !data.length ? (
        <EmptyState icon="book" title={t('cons.no_accounts')} body={t('cons.no_accounts_body')} />
      ) : (
        <>
          <input className="input cn-search" type="search" value={q} placeholder={t('cons.search_accounts')} aria-label={t('cons.search_accounts')} onChange={(e) => setQ(e.target.value)} />
          <Table
            head={[t('cons.code'), t('cons.account'), t('cons.type'), t('cons.mapped_codes'), '']}
            left={[1, 2]}
            phoneHide={[2, 3]}
            empty={t('cons.no_match')}
            rows={shown.map((a) => ({
              key: a.id,
              cells: [
                <span className="cn-code">{a.code}</span>,
                a.name,
                typeLabel(a.type),
                String(a.mapped),
                manage ? <IconButton icon="edit" label={t('cons.edit_account_n', { code: a.code })} onClick={() => setEdit(a)} /> : '',
              ],
            }))}
          />
        </>
      )}
      {edit ? <AccountDialog account={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={changed} /> : null}
    </section>
  );
}

function AccountDialog({ account, onClose, onSaved }: { account: GroupAccount | null; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ code: account?.code ?? '', name: account?.name ?? '', type: account?.type ?? ('asset' as AccountType) });
  const [drop, setDrop] = useState(false);
  return (
    <Dialog
      title={account ? t('cons.edit_account') : t('cons.add_account')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          {account ? (
            <Button variant="ghost" icon="trash" className="cn-left" onClick={() => setDrop(true)}>
              {t('cons.delete_account')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.code.trim() || !f.name.trim()}
            onClick={() =>
              void run(async () => {
                await action('consolidation.save_account', { id: account?.id ?? null, ...f });
                await onSaved();
                onClose();
              }, t('cons.account_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('cons.code')} required>
          <input className="input cn-code" value={f.code} maxLength={20} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
        <Field label={t('cons.account')} required>
          <input className="input" value={f.name} maxLength={160} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('cons.type')} hint={t('cons.type_hint')}>
          <select className="input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as AccountType })}>
            {TYPES.map((x) => (
              <option key={x} value={x}>
                {typeLabel(x)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {drop && account ? (
        <Confirm
          title={t('cons.delete_account_title', { code: account.code })}
          body={account.mapped ? tn('cons.delete_account_mapped', account.mapped) : t('cons.delete_account_body')}
          confirm={t('cons.delete_account')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('consolidation.delete_account', { id: account.id });
              await onSaved();
              onClose();
            }, t('cons.account_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

/** A subsidiary's side: its trial balance as a file for the head office. */
function SendTab() {
  const { busy, run } = useSave();
  const year = todayIso().slice(0, 4);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(todayIso());
  return (
    <section className="panel cn-send">
      <div className="cn-send-icon" aria-hidden="true">
        <Icon name="upload" size={28} />
      </div>
      <div className="form-stack">
        <div>
          <h2>{t('cons.send_title')}</h2>
          <p className="muted">{t('cons.send_lead')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('cons.from')} required>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('cons.to')} required>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <div className="cn-row-actions">
          <Button
            variant="primary"
            icon="download"
            busy={busy}
            disabled={!from || !to || to < from}
            onClick={() =>
              void run(async () => {
                const file = await action<{ business: string; lines: unknown[] }>('consolidation.export', { date_from: from, date_to: to });
                const name = `trial-balance-${from}-${to}.json`;
                const text = JSON.stringify(file, null, 1);
                if (native.saveFile(name, 'application/json', text)) return;
                const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }, t('cons.sent_file'))
            }
          >
            {t('cons.download_file')}
          </Button>
        </div>
        <p className="muted small">{t('cons.send_note')}</p>
      </div>
    </section>
  );
}

function PeriodDialog({ period, onClose, onSaved }: { period: PeriodRow | null; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const navigate = useNavigate();
  const year = todayIso().slice(0, 4);
  const [f, setF] = useState({ name: period?.name ?? year, date_from: period?.date_from ?? `${year}-01-01`, date_to: period?.date_to ?? `${year}-12-31` });
  return (
    <Dialog
      title={period ? t('cons.edit_period') : t('cons.new_period')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim() || !f.date_from || !f.date_to || f.date_to < f.date_from}
            onClick={() =>
              void run(async () => {
                const r = await action<{ id: string }>('consolidation.save_period', { id: period?.id ?? null, ...f });
                await onSaved();
                onClose();
                if (!period) navigate(`/consolidation/${r.id}`);
              }, t('cons.period_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('cons.period_name')} required>
          <input className="input" value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <div className="form-grid">
          <Field label={t('cons.from')} required>
            <input className="input" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
          </Field>
          <Field label={t('cons.to')} error={f.date_to && f.date_to < f.date_from ? t('cons.ends_before') : undefined} required>
            <input className="input" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
          </Field>
        </div>
        <p className="muted small">{t('cons.period_hint')}</p>
      </div>
    </Dialog>
  );
}

// ---- a period -------------------------------------------------------------------------------

type PTab = 'report' | 'balances' | 'eliminations';

export function ConsolidationPeriod() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { data: p, reload } = useAction<PeriodView>('consolidation.period', { id });
  const { data: r, reload: reloadReport } = useAction<Report>('consolidation.report', { period: id });
  const { busy, run } = useSave();
  const [edit, setEdit] = useState(false);
  const [drop, setDrop] = useState(false);
  const closed = p?.period.state === 'closed';
  const manage = can('consolidation.manage') && !closed;
  const tab = (['report', 'balances', 'eliminations'].includes(params.get('tab') ?? '') ? params.get('tab') : 'report') as PTab;
  const setTab = (k: PTab) => setParams(k === 'report' ? {} : { tab: k }, { replace: true });
  const changed = async () => {
    await Promise.all([reload(), reloadReport()]);
  };
  const tabs: { key: PTab; icon: IconName; badge?: number }[] = [
    { key: 'report', icon: 'chart', badge: r?.checks.length || undefined },
    { key: 'balances', icon: 'table' },
    { key: 'eliminations', icon: 'split', badge: p?.eliminations.length || undefined },
  ];
  return (
    <Shell>
      <div className="page reports cn-page">
        <Crumbs items={[{ to: '/consolidation', label: t('cons.title') }, { label: p?.period.name ?? '…' }]} />
        <div className="page-head">
          <div>
            <h1 className="cn-title">
              {p?.period.name ?? ' '}
              {p ? <StatusBadge tone={STATE[p.period.state].tone} icon={STATE[p.period.state].icon} label={t(`cons.st_${p.period.state}` as MessageKey)} /> : null}
            </h1>
            <p className="muted">
              {p ? `${day(p.period.date_from)} – ${day(p.period.date_to)} · ${t('cons.in_currency', { currency: p.currency })}` : ' '}
              {closed && p?.period.closed_at ? ` · ${t('cons.closed_on', { when: dateTime(p.period.closed_at) })}` : ''}
            </p>
          </div>
          {p && can('consolidation.manage') ? (
            <span className="cn-row-actions">
              {closed ? (
                <Button
                  icon="undo"
                  busy={busy}
                  onClick={() =>
                    void run(async () => {
                      await action('consolidation.reopen', { id });
                      await changed();
                    }, t('cons.reopened'))
                  }
                >
                  {t('cons.reopen')}
                </Button>
              ) : (
                <>
                  <IconButton icon="edit" label={t('cons.edit_period')} onClick={() => setEdit(true)} />
                  <IconButton icon="trash" label={t('cons.delete_period')} onClick={() => setDrop(true)} />
                  <Button
                    variant="primary"
                    icon="lock"
                    busy={busy}
                    disabled={!r || r.checks.length > 0}
                    title={r?.checks.length ? t('cons.close_blocked') : undefined}
                    onClick={() =>
                      void run(async () => {
                        await action('consolidation.close', { id });
                        await changed();
                      }, t('cons.closed_done'))
                    }
                  >
                    {t('cons.close')}
                  </Button>
                </>
              )}
            </span>
          ) : null}
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('cons.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`cons.ptab_${x.key}` as MessageKey)}
              {x.badge ? <span className="cn-tab-badge">{x.badge}</span> : null}
            </button>
          ))}
        </div>
        {!p || !r ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : tab === 'report' ? (
          <ReportTab p={p} r={r} onFix={(k) => setTab(k === 'unmapped' ? 'balances' : 'balances')} />
        ) : tab === 'balances' ? (
          <BalancesTab p={p} manage={manage} onChange={changed} />
        ) : (
          <EliminationsTab p={p} manage={manage} onChange={changed} />
        )}
        {edit && p ? <PeriodDialog period={p.period} onClose={() => setEdit(false)} onSaved={changed} /> : null}
        {drop && p ? (
          <Confirm
            title={t('cons.delete_period_title', { name: p.period.name })}
            body={t('cons.delete_period_body')}
            confirm={t('cons.delete_period')}
            danger
            onClose={() => setDrop(false)}
            onConfirm={() =>
              void run(async () => {
                await action('consolidation.delete_period', { id });
                navigate('/consolidation');
              }, t('cons.period_deleted'))
            }
          />
        ) : null}
      </div>
    </Shell>
  );
}

function CheckList({ checks, onFix }: { checks: Check[]; onFix: (k: Check['kind']) => void }) {
  if (!checks.length)
    return (
      <div className="callout callout-ok" role="status">
        <Icon name="check" size={18} />
        <div>
          <p>{t('cons.checks_ok')}</p>
        </div>
      </div>
    );
  return (
    <div className="callout callout-warn" role="status">
      <Icon name="alert" size={18} />
      <div>
        <p>{tn('cons.checks_open', checks.length)}</p>
        <ul className="cn-checks">
          {checks.map((c, i) => (
            <li key={i}>
              <button type="button" className="cn-link" onClick={() => onFix(c.kind)}>
                {c.kind === 'unmapped'
                  ? tn('cons.chk_unmapped', c.count ?? 0, { name: c.name, codes: (c.codes ?? []).join(', ') })
                  : c.kind === 'unbalanced'
                    ? t('cons.chk_unbalanced', { name: c.name, amount: money(Math.abs(c.count ?? 0)) })
                    : t(`cons.chk_${c.kind}` as MessageKey, { name: c.name })}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ReportTab({ p, r, onFix }: { p: PeriodView; r: Report; onFix: (k: Check['kind']) => void }) {
  const [view, setView] = useState<'statements' | 'trial'>('statements');
  const hasNci = r.pl.nci !== 0 || r.bs.nci !== 0;
  const csv = () =>
    downloadCsv(
      `consolidation-${p.period.name}.csv`,
      [t('cons.code'), t('cons.account'), t('cons.type'), ...r.entities.map((e) => e.name), t('cons.elimination'), t('cons.group_total')],
      r.rows.map((x) => [x.code, x.name || t('cons.unmapped_row'), x.type ? typeLabel(x.type) : '', ...r.entities.map((e) => plain(x.entities[e.id] ?? 0)), plain(x.elimination), plain(x.total)]),
    );
  return (
    <>
      <CheckList checks={r.checks} onFix={onFix} />
      <div className="rep-kpis">
        <Kpi label={t('cons.k_income')} value={money(r.pl.income)} />
        <Kpi label={t('cons.k_profit')} value={money(r.pl.profit)} hint={hasNci ? t('cons.k_profit_owners', { amount: money(r.pl.owners) }) : undefined} warn={r.pl.profit < 0} />
        <Kpi label={t('cons.k_assets')} value={money(r.bs.assets)} />
        <Kpi label={t('cons.k_difference')} value={money(r.bs.difference)} hint={r.bs.difference ? t('cons.k_difference_bad') : t('cons.k_difference_ok')} warn={r.bs.difference !== 0} />
      </div>
      <div className="cn-toolbar">
        <div className="segmented" role="group" aria-label={t('cons.view')}>
          {(['statements', 'trial'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
              {t(`cons.view_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="cn-row-actions">
          <Button size="sm" variant="ghost" icon="printer" onClick={() => window.print()}>
            {t('cons.print')}
          </Button>
          <Button size="sm" variant="ghost" icon="download" onClick={csv}>
            {t('cons.csv')}
          </Button>
        </span>
      </div>
      {view === 'statements' ? <Statements r={r} /> : <TrialBalance r={r} />}
      <section className="panel">
        <h2 className="cn-h2">{t('cons.by_company')}</h2>
        <Table
          head={[t('cons.company'), t('cons.ownership'), t('cons.method'), t('cons.profit'), t('cons.net_assets'), t('cons.translation')]}
          phoneHide={[1, 2, 4, 5]}
          rows={r.entities.map((e) => ({
            key: e.id,
            cells: [
              <strong>{e.name}</strong>,
              pctText(e.ownership),
              t(`cons.m_${e.method}` as MessageKey),
              e.profit === undefined ? '—' : money(e.profit),
              e.net_assets === undefined ? '—' : money(e.net_assets),
              e.currency === r.currency ? '—' : money(-(r.cta.entities[e.id] ?? 0)),
            ],
          }))}
        />
      </section>
    </>
  );
}

function StatementLine({ label, value, strong, indent, muted }: { label: string; value: number; strong?: boolean; indent?: boolean; muted?: boolean }) {
  return (
    <div className={`cn-line${strong ? ' strong' : ''}${indent ? ' indent' : ''}${muted ? ' muted' : ''}`}>
      <span>{label}</span>
      <span className="cn-num">{money(value)}</span>
    </div>
  );
}

function Statements({ r }: { r: Report }) {
  const of = (type: AccountType) => r.rows.filter((x) => x.type === type);
  const sign = (type: AccountType) => (type === 'asset' || type === 'expense' ? 1 : -1);
  const lines = (type: AccountType) =>
    of(type).map((x) => <StatementLine key={x.code} label={`${x.code} · ${x.name}`} value={sign(type) * x.total} indent muted={x.total === 0} />);
  return (
    <div className="cn-statements">
      <section className="panel">
        <h2 className="cn-h2">{t('cons.pl')}</h2>
        <h3 className="cn-h3">{t('cons.type_income')}</h3>
        {lines('income')}
        <StatementLine label={t('cons.total_income')} value={r.pl.income} strong />
        <h3 className="cn-h3">{t('cons.type_expense')}</h3>
        {lines('expense')}
        <StatementLine label={t('cons.total_expense')} value={r.pl.expense} strong />
        <div className="cn-sep" />
        <StatementLine label={t('cons.profit')} value={r.pl.profit} strong />
        {r.pl.nci ? (
          <>
            <StatementLine label={t('cons.profit_owners')} value={r.pl.owners} indent />
            <StatementLine label={t('cons.profit_nci')} value={r.pl.nci} indent />
          </>
        ) : null}
      </section>
      <section className="panel">
        <h2 className="cn-h2">{t('cons.bs')}</h2>
        <h3 className="cn-h3">{t('cons.type_asset')}</h3>
        {lines('asset')}
        <StatementLine label={t('cons.total_assets')} value={r.bs.assets} strong />
        <h3 className="cn-h3">{t('cons.type_liability')}</h3>
        {lines('liability')}
        <StatementLine label={t('cons.total_liabilities')} value={r.bs.liabilities} strong />
        <h3 className="cn-h3">{t('cons.type_equity')}</h3>
        {lines('equity')}
        <StatementLine label={t('cons.profit')} value={r.bs.profit} indent />
        {r.bs.translation ? <StatementLine label={t('cons.translation_reserve')} value={-r.bs.translation} indent /> : null}
        <StatementLine label={t('cons.total_equity')} value={r.bs.equity + r.bs.profit - r.bs.translation} strong />
        {r.bs.nci ? <StatementLine label={t('cons.of_which_nci')} value={r.bs.nci} indent muted /> : null}
        <div className="cn-sep" />
        <StatementLine label={t('cons.total_right')} value={r.bs.total_right} strong />
        {r.bs.unmapped ? <StatementLine label={t('cons.unmapped_row')} value={r.bs.unmapped} indent /> : null}
      </section>
    </div>
  );
}

function TrialBalance({ r }: { r: Report }) {
  const total = (fn: (x: ReportRow) => number) => r.rows.reduce((a, x) => a + fn(x), 0);
  return (
    <section className="panel cn-tb">
      <p className="muted small">{t('cons.tb_lead')}</p>
      <TableWrap>
        <Table
          head={[t('cons.account'), ...r.entities.map((e) => e.name), t('cons.elimination'), t('cons.group_total')]}
          phoneHide={r.entities.map((_, i) => i + 1).concat([r.entities.length + 1])}
          rows={r.rows.map((x) => ({
            key: x.account ?? 'unmapped',
            cells: [
              <span className={`cn-acc${x.account ? '' : ' cn-warn'}`}>
                <span className="cn-code">{x.code}</span>
                <span>{x.name || t('cons.unmapped_row')}</span>
              </span>,
              ...r.entities.map((e) => (x.entities[e.id] ? money(x.entities[e.id]!) : '—')),
              x.elimination ? money(x.elimination) : '—',
              <strong>{money(x.total)}</strong>,
            ],
          }))}
          foot={[
            t('cons.translation'),
            ...r.entities.map((e) => (r.cta.entities[e.id] ? money(r.cta.entities[e.id]!) : '—')),
            '—',
            money(r.cta.total),
          ]}
        />
      </TableWrap>
      <p className="muted small">{t('cons.tb_check', { amount: money(total((x) => x.total) + r.cta.total) })}</p>
    </section>
  );
}

function BalancesTab({ p, manage, onChange }: { p: PeriodView; manage: boolean; onChange: () => Promise<unknown> }) {
  const navigate = useNavigate();
  if (!p.entities.length)
    return (
      <section className="panel">
        <EmptyState icon="store" title={t('cons.no_companies')} body={t('cons.no_companies_body')} action={<Button onClick={() => navigate('/consolidation?tab=companies')}>{t('cons.add_company')}</Button>} />
      </section>
    );
  return (
    <div className="cn-cards">
      {p.entities.map((e) => (
        <EntityCard key={e.id} e={e} p={p} manage={manage} onChange={onChange} />
      ))}
    </div>
  );
}

function EntityCard({ e, p, manage, onChange }: { e: PeriodEntity; p: PeriodView; manage: boolean; onChange: () => Promise<unknown> }) {
  const navigate = useNavigate();
  const { busy, run } = useSave();
  const file = useRef<HTMLInputElement>(null);
  const [rates, setRates] = useState(false);
  const balanced = e.totals.debit === e.totals.credit;
  const state: { tone: Tone; icon: IconName; label: string } = !e.load
    ? { tone: 'warn', icon: 'clock', label: t('cons.waiting_balance') }
    : !balanced
      ? { tone: 'danger', icon: 'alert', label: t('cons.not_balanced') }
      : e.foreign && !e.rates
        ? { tone: 'warn', icon: 'percent', label: t('cons.needs_rates') }
        : { tone: 'ok', icon: 'check', label: t('cons.ready') };
  const importFile = async (f: File) => {
    await run(async () => {
      const r = await upload<{ lines: number; warning: string | null }>('consolidation.import', { period: p.period.id, entity: e.id, file: f.name, text: await f.text() });
      await onChange();
      if (r.warning === 'dates') throw new Error(t('cons.import_dates'));
    }, t('cons.imported'));
  };
  return (
    <section className="panel cn-card">
      <div className="cn-card-head">
        <div>
          <h2>{e.name}</h2>
          <p className="muted small">
            {e.currency} · {pctText(e.ownership)} · {t(`cons.m_${e.method}` as MessageKey)}
          </p>
        </div>
        <StatusBadge tone={state.tone} icon={state.icon} label={state.label} />
      </div>
      <dl className="cn-facts">
        <div>
          <dt>{t('cons.balance_from')}</dt>
          <dd>{e.load ? (e.load.source === 'ledger' ? t('cons.from_books') : e.load.source) : '—'}</dd>
        </div>
        <div>
          <dt>{t('cons.lines')}</dt>
          <dd>{e.load ? String(e.load.lines) : '—'}</dd>
        </div>
        <div>
          <dt>{t('cons.debit_credit')}</dt>
          <dd>{e.load ? `${moneyIn(e.totals.debit, e.currency)} / ${moneyIn(e.totals.credit, e.currency)}` : '—'}</dd>
        </div>
        <div>
          <dt>{t('cons.loaded_at')}</dt>
          <dd>{e.load ? dateTime(e.load.loaded_at) : '—'}</dd>
        </div>
        {e.foreign ? (
          <div>
            <dt>{t('cons.rates')}</dt>
            <dd>{e.rates ? t('cons.rates_text', { closing: rateText(e.rates.closing), average: rateText(e.rates.average), currency: e.currency, base: p.currency }) : '—'}</dd>
          </div>
        ) : null}
      </dl>
      <div className="cn-row-actions">
        {manage && e.source === 'own' ? (
          <Button
            icon="repeat"
            variant={e.load ? 'secondary' : 'primary'}
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action('consolidation.load_ledger', { period: p.period.id, entity: e.id });
                await onChange();
              }, t('cons.loaded'))
            }
          >
            {e.load ? t('cons.reload_books') : t('cons.load_books')}
          </Button>
        ) : null}
        {manage && e.source === 'import' ? (
          <>
            <Button icon="upload" variant={e.load ? 'secondary' : 'primary'} busy={busy} onClick={() => file.current?.click()}>
              {e.load ? t('cons.replace_file') : t('cons.import_file')}
            </Button>
            <input
              ref={file}
              type="file"
              accept=".json,.csv,.txt,application/json,text/csv"
              hidden
              onChange={(x) => {
                const f = x.target.files?.[0];
                x.target.value = '';
                if (f) void importFile(f);
              }}
            />
          </>
        ) : null}
        {manage && e.foreign ? (
          <Button icon="percent" variant={e.rates ? 'ghost' : 'secondary'} onClick={() => setRates(true)}>
            {e.rates ? t('cons.change_rates') : t('cons.enter_rates')}
          </Button>
        ) : null}
        <Button icon="link" variant="ghost" onClick={() => navigate(`/consolidation/mapping/${e.id}?period=${p.period.id}`)}>
          {t('cons.map_accounts')}
        </Button>
      </div>
      {manage && e.source === 'import' && !e.load ? <p className="muted small">{t('cons.import_hint')}</p> : null}
      {rates ? <RatesDialog e={e} p={p} onClose={() => setRates(false)} onSaved={onChange} /> : null}
    </section>
  );
}

function RatesDialog({ e, p, onClose, onSaved }: { e: PeriodEntity; p: PeriodView; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const start = (v: number | null | undefined) => (v ? String(v / 1_000_000) : '');
  const [closing, setClosing] = useState(start(e.rates?.closing ?? e.suggested?.closing));
  const [average, setAverage] = useState(start(e.rates?.average ?? e.suggested?.average));
  const c = parseRate(closing);
  const a = parseRate(average);
  return (
    <Dialog
      title={t('cons.rates_for', { name: e.name })}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!c || !a}
            onClick={() =>
              void run(async () => {
                await action('consolidation.save_rates', { period: p.period.id, entity: e.id, closing: toMillionths(c!), average: toMillionths(a!) });
                await onSaved();
                onClose();
              }, t('cons.rates_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('cons.rates_lead', { currency: e.currency, base: p.currency })}</p>
        <Field label={t('cons.closing_rate', { date: day(p.period.date_to) })} hint={t('cons.closing_hint')} error={closing && !c ? t('cons.rate_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={closing} onChange={(x) => setClosing(x.target.value)} />
        </Field>
        <Field label={t('cons.average_rate')} hint={t('cons.average_hint')} error={average && !a ? t('cons.rate_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={average} onChange={(x) => setAverage(x.target.value)} />
        </Field>
        {e.suggested?.closing ? <p className="muted small">{t('cons.rates_suggested')}</p> : <p className="muted small">{t('cons.rates_none')}</p>}
      </div>
    </Dialog>
  );
}

function EliminationsTab({ p, manage, onChange }: { p: PeriodView; manage: boolean; onChange: () => Promise<unknown> }) {
  const { data: accounts } = useAction<GroupAccount[]>('consolidation.chart');
  const [edit, setEdit] = useState<Elimination | 'new' | null>(null);
  const name = (id: string) => {
    const a = accounts?.find((x) => x.id === id);
    return a ? `${a.code} · ${a.name}` : '—';
  };
  return (
    <section className="panel">
      <div className="cn-panel-head">
        <div>
          <h2>{t('cons.eliminations')}</h2>
          <p className="muted small">{t('cons.elim_lead')}</p>
        </div>
        {manage ? (
          <Button icon="plus" onClick={() => setEdit('new')}>
            {t('cons.add_elim')}
          </Button>
        ) : null}
      </div>
      {!p.eliminations.length ? (
        <EmptyState icon="split" title={t('cons.no_elims')} body={t('cons.no_elims_body')} />
      ) : (
        <ul className="cn-elims">
          {p.eliminations.map((x) => (
            <li key={x.id}>
              <button type="button" className="cn-elim" disabled={!manage} onClick={() => setEdit(x)}>
                <span className="cn-elim-head">
                  <strong>{x.memo}</strong>
                  <span className="cn-num">{money(x.total)}</span>
                </span>
                <span className="cn-elim-lines">
                  {x.lines.map((l, i) => (
                    <span key={i}>
                      <span>{name(l.account)}</span>
                      <span className="cn-num muted">{l.debit ? `${t('cons.dr')} ${money(l.debit)}` : `${t('cons.cr')} ${money(l.credit)}`}</span>
                    </span>
                  ))}
                </span>
                <span className="muted small">{t('cons.elim_by', { name: x.by_name ?? '—', when: dateTime(x.created_at) })}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {edit && accounts ? <EliminationDialog elim={edit === 'new' ? null : edit} period={p.period.id} accounts={accounts} onClose={() => setEdit(null)} onSaved={onChange} /> : null}
    </section>
  );
}

function EliminationDialog({
  elim,
  period,
  accounts,
  onClose,
  onSaved,
}: {
  elim: Elimination | null;
  period: string;
  accounts: GroupAccount[];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { busy, run } = useSave();
  const fmt = (n: number) => (n ? String(n / 100) : '');
  const [memo, setMemo] = useState(elim?.memo ?? '');
  const [lines, setLines] = useState(
    elim?.lines.map((l) => ({ account: l.account, debit: fmt(l.debit), credit: fmt(l.credit) })) ?? [
      { account: '', debit: '', credit: '' },
      { account: '', debit: '', credit: '' },
    ],
  );
  const [drop, setDrop] = useState(false);
  const parsed = lines.map((l) => ({ account: l.account, debit: l.debit ? parseMoney(l.debit) : 0, credit: l.credit ? parseMoney(l.credit) : 0 }));
  const bad = parsed.some((l) => l.debit === null || l.credit === null);
  const debit = parsed.reduce((a, l) => a + (l.debit ?? 0), 0);
  const credit = parsed.reduce((a, l) => a + (l.credit ?? 0), 0);
  const used = parsed.filter((l) => l.debit || l.credit);
  const ok = !bad && memo.trim() && used.length >= 2 && used.every((l) => l.account && !(l.debit && l.credit)) && debit === credit && debit > 0;
  const setLine = (i: number, p: Partial<(typeof lines)[number]>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return (
    <Dialog
      title={elim ? t('cons.edit_elim') : t('cons.add_elim')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          {elim ? (
            <Button variant="ghost" icon="trash" className="cn-left" onClick={() => setDrop(true)}>
              {t('cons.delete_elim')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                await action('consolidation.save_elimination', { id: elim?.id ?? null, period, memo, lines: used });
                await onSaved();
                onClose();
              }, t('cons.elim_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('cons.elim_memo')} required>
          <input className="input" value={memo} maxLength={200} placeholder={t('cons.elim_memo_ph')} onChange={(e) => setMemo(e.target.value)} />
        </Field>
        <div className="cn-elim-grid" role="group" aria-label={t('cons.elim_lines')}>
          <span className="cn-grid-head">{t('cons.account')}</span>
          <span className="cn-grid-head">{t('cons.debit')}</span>
          <span className="cn-grid-head">{t('cons.credit')}</span>
          <span />
          {lines.map((l, i) => (
            <div key={i} className="cn-elim-row">
              <select className="input" aria-label={t('cons.line_account', { n: i + 1 })} value={l.account} onChange={(e) => setLine(i, { account: e.target.value })}>
                <option value="">{t('cons.choose_account')}</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} · {a.name}
                  </option>
                ))}
              </select>
              <input className="input cn-num" inputMode="decimal" aria-label={t('cons.line_debit', { n: i + 1 })} value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} />
              <input className="input cn-num" inputMode="decimal" aria-label={t('cons.line_credit', { n: i + 1 })} value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} />
              <IconButton icon="x" label={t('cons.remove_line', { n: i + 1 })} disabled={lines.length <= 2} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
            </div>
          ))}
          <div className="cn-elim-row cn-elim-total">
            <span>{t('cons.totals')}</span>
            <span className="cn-num">{money(debit)}</span>
            <span className="cn-num">{money(credit)}</span>
            <span />
          </div>
        </div>
        <div className="cn-row-actions">
          <Button size="sm" icon="plus" disabled={lines.length >= 50} onClick={() => setLines([...lines, { account: '', debit: '', credit: '' }])}>
            {t('cons.add_line')}
          </Button>
          {debit !== credit ? <span className="cn-warn small">{t('cons.out_by', { amount: money(Math.abs(debit - credit)) })}</span> : null}
        </div>
      </div>
      {drop && elim ? (
        <Confirm
          title={t('cons.delete_elim_title')}
          body={t('cons.delete_elim_body', { memo: elim.memo })}
          confirm={t('cons.delete_elim')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('consolidation.delete_elimination', { id: elim.id });
              await onSaved();
              onClose();
            }, t('cons.elim_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- mapping --------------------------------------------------------------------------------

interface MappingView {
  entity: Entity;
  codes: { code: string; name: string | null; account: string | null }[];
}

export function ConsolidationMapping() {
  const { entity = '' } = useParams();
  const [params] = useSearchParams();
  const back = params.get('period');
  const { can } = useSession();
  const manage = can('consolidation.manage');
  const { data: m, reload } = useAction<MappingView>('consolidation.mapping', { entity });
  const { data: accounts } = useAction<GroupAccount[]>('consolidation.chart');
  const { busy, run } = useSave();
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [only, setOnly] = useState(false);
  const [q, setQ] = useState('');
  const value = (code: string, saved: string | null) => (code in draft ? draft[code]! : saved);
  const dirty = m ? m.codes.filter((c) => c.code in draft && draft[c.code] !== c.account) : [];
  const left = m ? m.codes.filter((c) => !value(c.code, c.account)).length : 0;
  const shown = (m?.codes ?? []).filter((c) => {
    if (only && value(c.code, c.account)) return false;
    const s = q.trim().toLowerCase();
    return !s || c.code.toLowerCase().includes(s) || (c.name ?? '').toLowerCase().includes(s);
  });
  return (
    <Shell>
      <div className="page reports cn-page">
        <Crumbs
          items={[
            { to: '/consolidation', label: t('cons.title') },
            ...(back ? [{ to: `/consolidation/${back}?tab=balances`, label: t('cons.period') }] : [{ to: '/consolidation?tab=companies', label: t('cons.companies') }]),
            { label: m?.entity.name ?? '…' },
          ]}
        />
        <div className="page-head">
          <div>
            <h1>{t('cons.mapping_title', { name: m?.entity.name ?? '' })}</h1>
            <p className="muted">{m ? (left ? tn('cons.mapping_left', left) : t('cons.mapping_done')) : ' '}</p>
          </div>
          {manage && m ? (
            <span className="cn-row-actions">
              <Button
                icon="sparkle"
                busy={busy}
                disabled={!left}
                onClick={() =>
                  void run(async () => {
                    const r = await action<{ mapped: number }>('consolidation.auto_map', { entity });
                    setDraft({});
                    await reload();
                    return r;
                  }, t('cons.auto_mapped'))
                }
              >
                {t('cons.auto_map')}
              </Button>
              <Button
                variant="primary"
                icon="check"
                busy={busy}
                disabled={!dirty.length}
                onClick={() =>
                  void run(async () => {
                    await action('consolidation.save_mapping', { entity, items: dirty.map((c) => ({ code: c.code, account: draft[c.code] ?? null })) });
                    setDraft({});
                    await reload();
                  }, t('cons.mapping_saved'))
                }
              >
                {dirty.length ? tn('cons.save_n', dirty.length) : t('common.save')}
              </Button>
            </span>
          ) : null}
        </div>
        {!m || !accounts ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : !m.codes.length ? (
          <section className="panel">
            <EmptyState icon="link" title={t('cons.no_codes')} body={t('cons.no_codes_body')} />
          </section>
        ) : (
          <section className="panel">
            <p className="muted small">{t('cons.mapping_lead')}</p>
            <div className="cn-toolbar">
              <input className="input cn-search" type="search" value={q} placeholder={t('cons.search_codes')} aria-label={t('cons.search_codes')} onChange={(e) => setQ(e.target.value)} />
              <label className="check-row">
                <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} />
                <span>{t('cons.only_unmapped')}</span>
              </label>
            </div>
            <Table
              head={[t('cons.their_code'), t('cons.their_name'), t('cons.group_account')]}
              left={[1, 2]}
              phoneHide={[1]}
              empty={t('cons.no_match')}
              rows={shown.map((c) => {
                const v = value(c.code, c.account);
                return {
                  key: c.code,
                  cells: [
                    <span className="cn-code">{c.code}</span>,
                    c.name ?? '—',
                    <select
                      className={`input cn-map${v ? '' : ' cn-map-empty'}`}
                      aria-label={t('cons.map_code', { code: c.code })}
                      value={v ?? ''}
                      disabled={!manage}
                      onChange={(e) => setDraft({ ...draft, [c.code]: e.target.value || null })}
                    >
                      <option value="">{t('cons.not_mapped')}</option>
                      {TYPES.map((ty) => (
                        <optgroup key={ty} label={typeLabel(ty)}>
                          {accounts
                            .filter((a) => a.type === ty)
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.code} · {a.name}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>,
                  ],
                };
              })}
            />
          </section>
        )}
      </div>
    </Shell>
  );
}
